import type { ParsedDocument } from '@vif/parsers';
import type { DocumentFormat } from '../storage/types.js';
import type { WorkerRequest, WorkerResponse } from './parse-worker.js';
import type { ParseFn } from './import-document.js';

/**
 * Main-thread client for the parse Worker (§6.7, PD-2). One worker per
 * parse job: cancellation is a cooperative message first (lets pdf.js abort
 * between pages) and a hard terminate() on settle, so a hung parse can
 * never leak a thread.
 */
export function createWorkerParse(): ParseFn {
  let nextId = 1;
  return (format, bytes, options) =>
    new Promise<ParsedDocument>((resolve, reject) => {
      const id = nextId++;
      const worker = new Worker(new URL('./parse-worker.ts', import.meta.url), {
        type: 'module',
      });
      let settled = false;

      const settle = (fn: () => void): void => {
        if (settled) return;
        settled = true;
        clearInterval(abortPoll);
        worker.terminate();
        fn();
      };

      // The structural signal has no event; poll it (import UI cadence).
      const abortPoll = window.setInterval(() => {
        if (options?.signal?.aborted === true) {
          const cancel: WorkerRequest = { id, type: 'cancel' };
          worker.postMessage(cancel);
          // Give the worker one beat to abort cooperatively, then hard-stop.
          window.setTimeout(
            () =>
              settle(() => reject(Object.assign(new Error('cancelled'), { name: 'AbortError' }))),
            250,
          );
        }
      }, 100);

      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const message = event.data;
        if (message.id !== id) return;
        if (message.type === 'progress') {
          options?.onProgress?.(message.page, message.total);
        } else if (message.type === 'done') {
          settle(() => resolve(message.parsed));
        } else {
          const error = Object.assign(new Error(message.message), {
            name: message.name,
            ...(message.code === undefined ? {} : { code: message.code }),
          });
          settle(() => reject(error));
        }
      };
      worker.onerror = (event) => {
        settle(() => reject(new Error(event.message || 'parse worker failed')));
      };

      const request: WorkerRequest = { id, type: 'parse', format, name: '', bytes };
      worker.postMessage(request);
    });
}

export type { DocumentFormat };
